'use client'

import { useEffect, useState } from 'react'

import { Amplify } from 'aws-amplify'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { CookieStorage } from 'aws-amplify/utils'
import { getCookie } from 'cookies-next/client'

import { COOKIE_DAYS_TO_EXPIRE } from '@/constants/config'
import { ACCOUNT_SUBDOMAIN } from '@/constants/routes'
import { COOKIE_CONFIG } from '@/lib/cookies/cookie-config'

type AmplifyProviderProps = Readonly<{
  children: React.ReactNode
  subdomain?: string
}>

let configuredSubdomain: string | undefined

/** Configures Amplify once per subdomain. Returns an error message, or null. */
const configureAmplifyOnce = (subdomain?: string): string | null => {
  if (subdomain === ACCOUNT_SUBDOMAIN || configuredSubdomain === subdomain) return null

  try {
    const configString = getCookie(`amplify-config-${subdomain}`)

    if (!configString) {
      throw new Error(`No Amplify config found for subdomain: ${subdomain}`)
    }

    const config = JSON.parse(configString)

    if (!config.userPoolId || !config.clientId) {
      throw new Error('Invalid Amplify config: missing required fields')
    }

    Amplify.configure(
      {
        Auth: {
          Cognito: {
            userPoolId: config.userPoolId,
            userPoolClientId: config.clientId,
          },
        },
      },
      {
        ssr: true,
      },
    )

    // Configure cookie storage - MUST be after Amplify.configure
    const cookieStorage = new CookieStorage({
      ...COOKIE_CONFIG,
      expires: COOKIE_DAYS_TO_EXPIRE,
    })

    cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage)

    configuredSubdomain = subdomain
    return null
  } catch (err) {
    console.error('Failed to configure Amplify:', err)
    return err instanceof Error ? err.message : 'Configuration failed'
  }
}

function AmplifyProvider(props: AmplifyProviderProps) {
  const { subdomain, children } = props

  // Configured during the first client render, not in an effect: child effects run before
  // this provider's effects, and they may call Amplify. The server does not need it.
  const [configError] = useState(() =>
    typeof window === 'undefined' ? null : configureAmplifyOnce(subdomain),
  )
  // Shown after mount, so the first client render matches the server HTML.
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(configError)
  }, [configError])

  if (error) {
    return (
      <div style={{ padding: '2rem', color: 'red' }}>
        <h2>Configuration Error</h2>
        <p>{error}</p>
      </div>
    )
  }

  return <>{children}</>
}

export default AmplifyProvider
